/**
 * Phase 8.3 — Pre-prod warnings banner for admin surfaces.
 *
 * Fetches /api/preprod/warnings and renders a dismissible (per-session) but
 * reappears-on-reload banner at the top of /admin-book. If `warnings.count`
 * is 0, nothing renders.
 *
 * Uses the same ADMIN_API_KEY as the surrounding admin page — expects the
 * caller to pass it in.
 */
import React, { useEffect, useState } from 'react'
import { AlertTriangle, X, ChevronRight } from 'lucide-react'

const SEVERITY = {
  high: { bg: '#FEF3C7', border: '#D97706', text: '#78350F' },
  medium: { bg: '#FFFBEB', border: '#B45309', text: '#92400E' },
  low: { bg: '#F0F9FF', border: '#0369A1', text: '#075985' },
}

export default function PreProdWarnings({ apiUrl, adminKey }) {
  const [data, setData] = useState(null)
  const [dismissed, setDismissed] = useState(false)
  const [open, setOpen] = useState(true)

  useEffect(() => {
    if (!apiUrl || !adminKey) return
    fetch(`${apiUrl}/api/preprod/warnings?admin_key=${encodeURIComponent(adminKey)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setData)
      .catch(() => setData({ warnings: [], count: 0 }))
  }, [apiUrl, adminKey])

  if (!data || data.count === 0 || dismissed) return null

  return (
    <div
      role="alert"
      data-testid="preprod-warnings-banner"
      style={{
        background: SEVERITY.high.bg,
        border: `1px solid ${SEVERITY.high.border}`,
        color: SEVERITY.high.text,
        borderRadius: 10,
        marginBottom: 20,
        overflow: 'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', gap: 12 }}>
        <AlertTriangle size={20} style={{ flexShrink: 0, color: SEVERITY.high.border }} />
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>
          {data.count} pre-production item{data.count === 1 ? '' : 's'} still need
          {data.count === 1 ? 's' : ''} your attention before PPC launch.
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="preprod-warnings-toggle"
          style={{
            background: 'transparent',
            border: 'none',
            color: SEVERITY.high.text,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 500,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '4px 8px',
          }}
        >
          {open ? 'Hide' : 'Show'}
          <ChevronRight
            size={14}
            style={{
              transform: open ? 'rotate(90deg)' : 'rotate(0)',
              transition: 'transform 150ms',
            }}
          />
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss for this session (reappears on reload)"
          title="Dismiss for this session (reappears on reload)"
          data-testid="preprod-warnings-dismiss"
          style={{
            background: 'transparent',
            border: 'none',
            color: SEVERITY.high.text,
            cursor: 'pointer',
            padding: 4,
            opacity: 0.7,
          }}
        >
          <X size={16} />
        </button>
      </div>
      {open && (
        <div
          style={{
            borderTop: `1px solid ${SEVERITY.high.border}33`,
            padding: '8px 16px 12px 48px',
            background: '#FFF8E7',
          }}
        >
          {data.warnings.map((w) => (
            <div
              key={w.id}
              data-testid={`preprod-warning-${w.id}`}
              style={{ fontSize: 12, padding: '6px 0', lineHeight: 1.5 }}
            >
              <div style={{ fontWeight: 600 }}>{w.title}</div>
              <div style={{ color: SEVERITY.high.text + 'CC' }}>{w.detail}</div>
              {w.subjects && (
                <div style={{ marginTop: 2, fontFamily: 'monospace', fontSize: 11 }}>
                  {w.subjects.join(' · ')}
                </div>
              )}
              <div style={{ marginTop: 2, fontStyle: 'italic', color: SEVERITY.high.text + 'AA' }}>
                → {w.action}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
